import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon/Icon";
import X from "@insite/mobius/Icons/X";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ImageMagnifier from "@tSurya/Components/Imagemagnifire";
import PlayIcon from "@tSurya/Icons/PlayIcon";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    selectedIndexImage: state.pages.productDetails.selectedImageIndex,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    iframeContainer?: InjectableCss;
    carousalWrapper?: InjectableCss;
    carousalContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    fullPrimaryImageModal?: ModalPresentationProps;
    modalContainer?: InjectableCss;
    primaryImageConatiner?: InjectableCss;
    iframePrimaryImageConatiner?: InjectableCss;
    videoPlayerContainer?: InjectableCss;
    crossIconvideo?: InjectableCss;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    crossIconvideo: {
        css: css`
            cursor: pointer;
            padding: 5px;
            margin: 10px;
            margin-right: 20px;
            border: 1px solid ${getColor("common.border")};
            width: 50px;
            margin-left: auto;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: 100% !important;
                height: 100% !important;
                padding: 0px;
            `,
            modalBody: css`
                width: 100%;
                height: 100vh !important;
                max-height: 100% !important;
                max-width: 100% !important;
                margin: 0px !important;
                position: relative;
                > div[data-test-selector^="modal_ModalContent"] {
                    img {
                        max-height: 100%;
                    }
                }
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 0px;
                display: none;
            `,
            modalContent: css`
                text-align: center;
                position: relative;
                display: flex;
                flex-direction: row-reverse;
                flex-basis: auto;
                max-width: auto;
                width: auto;
                padding: 0px;
                justify-content: flex-end;
            `,
        },
    },
    modalContainer: {
        css: css`
            width: 100%;
        `,
    },
    videoPlayerContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            iframe {
                height: 92vh !important;
            }
        `,
    },
    primaryImageConatiner: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            iframe {
                height: 97vh;
                width: 100vh;
            }
        `,
    },
    iframePrimaryImageConatiner: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: relative;
                        width: 100%;
                        height: 0;
                        margin-left: 20px;
                        padding-bottom: 56.25%; /* 16:9 aspect ratio */
                    `,
                    css`
                        position: relative;
                        width: 100%;
                        height: 0;
                        margin-left: 20px;
                        padding-bottom: 56.25%; /* 16:9 aspect ratio */
                    `,
                    css`
                        position: relative;
                        width: 100%;
                        height: 0;
                        margin-left: 20px;
                        padding-bottom: 56.25%; /* 16:9 aspect ratio */
                    `,
                    null,
                    null,
                ])}
            iframe {
                height: 97vh;
                width: 100vh;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                        `,
                        css`
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                        `,
                        css`
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 0px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 100vh;
                    `,
                    css`
                        height: 100vh;
                    `,
                    css`
                        height: 100vh;
                    `,
                    null,
                    null,
                ])}
            img {
                height: 100vh;
                width: 100%;
                object-fit: contain;
            }
            iframe {
                height: 100vh;
                width: 100%;
                object-fit: contain;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            min-height: 197px;
                            height: auto;
                            width: 50%;
                            border: none !important;
                        `,
                        css`
                            min-height: 300px;
                            height: auto;
                            width: 50%;
                        `,
                        css`
                            min-height: 300px;
                            height: auto;
                            width: 75%;
                        `,
                        null,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 392px;
                            img {
                                max-height: 392px;
                            }
                        `,
                        css`
                            max-height: 392px;
                            img {
                                max-height: 392px;
                            }
                        `,
                        css`
                            max-height: 392px;
                            img {
                                max-height: 392px;
                            }
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
    iframeContainer: {
        css: css`
            width: 700px;
            max-width: 100%;
            /* height: 92vh; */
            position: relative;
        `,
    },
    carousalWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carousalContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 100%;
            padding: 0;
            position: relative;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
            > span {
                display: block;
            }
            img {
                height: 100vh;
                object-fit: contain;
            }
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    selectedIndexImage,
    setSelectedImageIndex,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [videPlayerModalIsOpen, setVidePlayerModalIsOpen] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const slideSwipeHandler = () => {
        setSelectedImageIndex({
            index: embla?.selectedScrollSnap() || 0,
        });
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", slideSwipeHandler);
    }, [embla]);

    useEffect(() => {
        if (!embla || !product.images) {
            return;
        }
        embla.reInit({
            slidesToScroll: 1,
            loop: true,
            startIndex: selectedIndexImage,
        });
    }, [embla, product, selectedIndexImage]);

    // const videoRegex = /^[0-9]{1,2}\.(?:mp4|mpg|avi)$/;

    // const viemoRegex =
    /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;
    const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/;

    if (!product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (youtubeRegex.test(productImage.mediumImagePath) && youtubeRegex.test(productImage.smallImagePath)) {
            return `https://via.placeholder.com/85x85`;
        }
        if (productImage.imageType === "Static") {
            return path;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    return (
        <>
            <StyledWrapper {...styles.carousalWrapper}>
                <StyledWrapper {...styles.carousalContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.carouselSlidesContainer}>
                        {product?.images?.map((productImage, index) => (
                            <StyledWrapper {...styles.carouselSlide} key={productImage.id} className="embla-slides">
                                <StyledWrapper {...styles.carouselSlideInner}>
                                    <Clickable
                                        css={css`
                                            width: 100%;
                                            /* background-color: ${getColor("common.backgroundContrast")}; */
                                            img {
                                                /* mix-blend-mode: multiply; */
                                            }
                                        `}
                                    >
                                        {image.imageType === "Static" && (
                                            <StyledWrapper
                                                {...styles.centeringWrapper}
                                                onClick={imageWrapperClickHandler}
                                            >
                                                {youtubeRegex.test(productImage.mediumImagePath) ? (
                                                    <StyledWrapper {...styles.iframeContainer}>
                                                        <StyledWrapper
                                                            onClick={() => setVidePlayerModalIsOpen(true)}
                                                            style={{
                                                                position: "absolute",
                                                                background: "transparent",
                                                                cursor: "pointer",
                                                                zIndex: 9,
                                                            }}
                                                        ></StyledWrapper>
                                                        <iframe
                                                            src={`https://www.youtube.com/embed/${productImage.mediumImagePath}`}
                                                            width="100%"
                                                            height="auto"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        ></iframe>
                                                        <Modal
                                                            {...styles.fullPrimaryImageModal}
                                                            isOpen={videPlayerModalIsOpen}
                                                            // handleClose={fullPrimaryImageModalCloseHandler}
                                                        >
                                                            <StyledWrapper {...styles.modalContainer}>
                                                                <StyledWrapper {...styles.videoPlayerContainer}>
                                                                    <StyledWrapper {...styles.crossIconvideo}>
                                                                        <Icon
                                                                            src={X}
                                                                            onClick={() =>
                                                                                setVidePlayerModalIsOpen(false)
                                                                            }
                                                                        ></Icon>
                                                                    </StyledWrapper>
                                                                    <StyledWrapper>
                                                                        <iframe
                                                                            src={`https://www.youtube.com/embed/${productImage.mediumImagePath}?autoplay=1`}
                                                                            width="100%"
                                                                            height="100%"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                        ></iframe>
                                                                    </StyledWrapper>
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </Modal>
                                                    </StyledWrapper>
                                                ) : (
                                                    <StyledWrapper
                                                        {...styles.centeringWrapper}
                                                        onClick={imageWrapperClickHandler}
                                                    >
                                                        <Img
                                                            {...styles.img}
                                                            src={getProductImageThumbPath(productImage)}
                                                            altText={image.imageAltText}
                                                            loading="eager"
                                                            data-test-selector="productDetails_mainImage"
                                                            onKeyPress={imageWrapperKeyHandler}
                                                            tabIndex={0}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                            </StyledWrapper>
                                        )}
                                    </Clickable>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                    {youtubeRegex.test(path) ? (
                        <StyledWrapper {...styles.iframeContainer}>
                            <iframe
                                src={`https://www.youtube.com/embed/${path}`}
                                width="100%"
                                height="100%"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                            <Img
                                {...styles.img}
                                src={path}
                                altText={image.imageAltText}
                                loading="eager"
                                data-test-selector="productDetails_mainImage"
                                onKeyPress={imageWrapperKeyHandler}
                                tabIndex={0}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )} */}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductPrimaryImage);
